import { CreativeSummaryModel } from './Creative'
import { InfeedModel } from './Infeed'
import { SearchResult } from '@/models/Repository'
import CreativeApi from '@/api/Creative'
import { BulkActionError } from './Error'
import { runInBatches } from '@/models/runInBatches'

export class CreativeRepository {
  /**
   * @typedef {("banner"|"in feed"|"video"|"three pas")} CreativeType
   * @param {{ ownerProfileId: number, creativeFolderIds: number[], creativeTypes?: CreativeType[], keyword?: string}} param0
   * @return {import { SearchResult } from '@/models/Repository'}
   */
  static async search({
    ownerProfileId,
    creativeFolderIds,
    creativeTypes,
    auditStatuses,
    keyword,
    pageNo,
    pageSize,
    sortKey,
    sortDirection,
  }) {
    try {
      const res = await CreativeApi.search(
        ownerProfileId,
        creativeFolderIds,
        creativeTypes,
        auditStatuses,
        keyword,
        pageNo,
        pageSize,
        sortKey,
        sortDirection,
      )
      return new SearchResult({
        count: res.count,
        items: res.items
          .map(item => ({ ...item, ownerProfileId }))
          .map(CreativeSummaryModel.assign),
        pageNo: res.pageNo,
        pageSize: pageSize,
      })
    } catch (e) {
      console.error(e)
      throw e
    }
  }

  static async findByIdsWithFolderInfo(idsWithFolderInfo) {
    const creativeFolderMap = new Map(
      idsWithFolderInfo.reduce((acc, idWithFolderInfo) => {
        const { ownerProfileId, creativeFolderId } = idWithFolderInfo
        const creativeFolders = acc.find(([key]) => key === ownerProfileId)
        if (creativeFolders) creativeFolders[1].push(creativeFolderId)
        else acc.push([ownerProfileId, [creativeFolderId]])
        return acc
      }, []),
    )
    const genRunner = (ownerProfileId, creativeFolderIds) => async () => {
      const ids = idsWithFolderInfo
        .filter(({ ownerProfileId: opid }) => opid === ownerProfileId)
        .map(({ id }) => id)
      const result = await CreativeRepository.search({
        ownerProfileId,
        creativeFolderIds,
      })
      return result.items.filter(c => ids.includes(c.id))
    }
    try {
      const creativesPool = await runInBatches(
        Array.from(
          creativeFolderMap.entries(),
          ([ownerProfileId, creativeFolderIds]) =>
            genRunner(ownerProfileId, creativeFolderIds),
        ),
      )
      return creativesPool.flat()
    } catch (e) {
      console.error(e)
      throw new BulkActionError('クリエイティブの取得に失敗しました')
    }
  }

  static async delete(id) {
    try {
      await CreativeApi.delete([id])
      return true
    } catch (e) {
      console.error(e)
      throw e
    }
  }

  static async bulkDelete(ids) {
    const genRunner = id => () => CreativeApi.delete(id)
    try {
      await runInBatches(ids.map(genRunner))
      return true
    } catch (e) {
      console.error(e)
      throw new BulkActionError(
        '一括削除に失敗しました。一部削除できていないものがあります',
      )
    }
  }

  static async bulkUpdate(creatives) {
    const genRunner = creative => () =>
      CreativeApi.update(creative.id, creative)
    try {
      const results = await runInBatches(creatives.map(genRunner))
      return results.map(result => CreativeSummaryModel.assign(result.item))
    } catch (e) {
      console.error(e)
      throw new BulkActionError(
        '一括変更に失敗しました。一部完了できていない操作があります',
      )
    }
  }

  /**
   * 一括フォルダ移動
   * @param {number}} creativeFolderId 移動先フォルダID
   * @param {number[]} ids
   */
  static async bulkMove(creativeFolderId, ids) {
    const creatives = ids.map(id => ({ id, creativeFolderId }))
    try {
      return await this.bulkUpdate(creatives)
    } catch (e) {
      throw new BulkActionError(
        '一括フォルダ移動に失敗しました。一部移動できていません。',
      )
    }
  }

  /**
   * テーブル内でのステータスや名前の変更時
   * @param {number} id
   * @param {{ name?: string, status?: StatusType, creativeFolderId?: number}} data
   */
  static async update(id, data) {
    try {
      const result = await CreativeApi.update(id, data)
      return CreativeSummaryModel.assign(result.item)
    } catch (e) {
      console.error(e)
      throw e
    }
  }

  /**
   * @param {number} id
   * @param {string} name
   */
  static async updateName(id, name) {
    try {
      return await this.update(id, { name })
    } catch (e) {
      console.error(e)
      throw new Error(`クリエイティブ名の変更に失敗しました`)
    }
  }

  /**
   * @typedef {("active" | "inactive")} StatusType
   * @param {number} id
   * @param {StatusType} status
   */
  static async updateStatus(id, status) {
    try {
      return await this.update(id, { status })
    } catch (e) {
      console.error(e)
      throw new Error(`ステータスの変更に失敗しました`)
    }
  }

  // =======================================================
  // バナー
  // =======================================================

  // =======================================================
  // インフィード
  // =======================================================

  static async getInfeed(id) {
    try {
      const res = await CreativeApi.getInfeed(id)
      return InfeedModel.assign(res)
    } catch (e) {
      console.error(e)
      throw e
    }
  }
}
